<script setup>
import { ref, computed, watch } from 'vue';
import {
  startOfDay,
  endOfDay,
  startOfMonth,
  addMonths,
  subMonths,
  isSameDay,
  fromUnixTime,
} from 'date-fns';
import {
  monthName,
  yearName,
  getWeeksForMonth,
  isToday,
  dayIsInRange,
  isCurrentMonth,
  isLastDayOfMonth,
  isHoveringDayInRange,
  isHoveringNextDayInRange,
} from './helpers/DatePickerHelper';
import CalendarWeekLabel from './components/CalendarWeekLabel.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  // Unix timestamps (seconds), or null
  startDate: { type: Number, default: null },
  endDate: { type: Number, default: null },
});

const emit = defineEmits(['select']);

const today = new Date();
const currentMonth = ref(startOfMonth(today));
const hoveredDate = ref(null);
const selectingEnd = ref(false);

const internalStart = ref(
  props.startDate ? startOfDay(fromUnixTime(props.startDate)) : null
);
const internalEnd = ref(
  props.endDate ? endOfDay(fromUnixTime(props.endDate)) : null
);

// Sync from parent only when NOT in the middle of an active range selection
watch(
  () => [props.startDate, props.endDate],
  ([newFrom, newTo]) => {
    if (selectingEnd.value) return;
    internalStart.value = newFrom ? startOfDay(fromUnixTime(newFrom)) : null;
    internalEnd.value = newTo ? endOfDay(fromUnixTime(newTo)) : null;
    if (internalStart.value) {
      currentMonth.value = startOfMonth(internalStart.value);
    }
  }
);

const weeks = computed(() => getWeeksForMonth(currentMonth.value));

const prevMonth = () => {
  currentMonth.value = subMonths(currentMonth.value, 1);
};

const nextMonth = () => {
  currentMonth.value = addMonths(currentMonth.value, 1);
};

const selectDate = day => {
  const d = startOfDay(day);

  if (!selectingEnd.value) {
    // First click: select single day, enter range-extension mode
    internalStart.value = d;
    internalEnd.value = null;
    selectingEnd.value = true;
    emit('select', { from: d, to: endOfDay(day), confirmed: false });
  } else if (isSameDay(day, internalStart.value)) {
    // Clicked same day again → confirm single day, exit selection mode
    internalEnd.value = endOfDay(day);
    selectingEnd.value = false;
    emit('select', { from: d, to: internalEnd.value, confirmed: true });
  } else if (day < internalStart.value) {
    // Clicked before start → restart from new day
    internalStart.value = d;
    internalEnd.value = null;
    emit('select', { from: d, to: endOfDay(day), confirmed: false });
  } else {
    // Second click after start → confirm range
    internalEnd.value = endOfDay(day);
    selectingEnd.value = false;
    emit('select', {
      from: internalStart.value,
      to: internalEnd.value,
      confirmed: true,
    });
  }
};

const isSelectedStartOrEnd = day =>
  dayIsInRange(day, internalStart.value, internalStart.value) ||
  (internalEnd.value &&
    dayIsInRange(day, internalEnd.value, internalEnd.value));

const isInRange = day =>
  internalEnd.value
    ? dayIsInRange(day, internalStart.value, internalEnd.value)
    : false;

// isHoveringDayInRange expects (day, startDate, selectingEndBool, hoveredDate)
const isHovering = day =>
  isHoveringDayInRange(
    day,
    internalStart.value,
    selectingEnd.value,
    hoveredDate.value
  );

// isHoveringNextDayInRange expects (day, startDate, endDate, hoveredDate)
const isNextHovering = day =>
  isHoveringNextDayInRange(
    day,
    internalStart.value,
    internalEnd.value,
    hoveredDate.value
  );

const isInMonth = day => isCurrentMonth(day, currentMonth.value);

const dayClasses = day => ({
  'text-n-slate-10 pointer-events-none': !isInMonth(day),
  'text-n-slate-12 hover:text-n-slate-12 hover:bg-n-blue-6 dark:hover:bg-n-blue-7':
    isInMonth(day),
  'bg-n-brand text-white': isSelectedStartOrEnd(day) && isInMonth(day),
  'bg-n-blue-4 dark:bg-n-blue-5':
    (isInRange(day) || isHovering(day)) &&
    !isSelectedStartOrEnd(day) &&
    isInMonth(day),
  'outline outline-1 outline-n-blue-8 -outline-offset-1 !text-n-blue-11':
    isToday(today, day) && !isSelectedStartOrEnd(day),
});
</script>

<template>
  <div class="flex flex-col gap-2 select-none w-full">
    <!-- Month navigation header -->
    <div class="flex items-center justify-between h-9">
      <NextButton
        slate
        ghost
        xs
        icon="i-lucide-chevron-left"
        @click="prevMonth"
      />
      <span class="text-sm font-medium text-n-slate-12">
        {{ monthName(currentMonth) }} {{ yearName(currentMonth) }}
      </span>
      <NextButton
        slate
        ghost
        xs
        icon="i-lucide-chevron-right"
        @click="nextMonth"
      />
    </div>

    <!-- Day-of-week labels -->
    <CalendarWeekLabel />

    <!-- Day grid -->
    <div
      v-for="week in weeks"
      :key="week[0].getTime()"
      class="grid max-w-md grid-cols-7 gap-2 mx-auto overflow-hidden rounded-lg"
    >
      <div
        v-for="day in week"
        :key="day.getTime()"
        class="flex relative items-center justify-center w-9 h-8 py-1.5 px-2 font-medium text-sm rounded-lg cursor-pointer"
        :class="dayClasses(day)"
        @mouseenter="hoveredDate = day"
        @mouseleave="hoveredDate = null"
        @click="selectDate(day)"
      >
        {{ day.getDate() }}
        <!-- Range connector span between consecutive days -->
        <span
          v-if="
            (isInRange(day) || isHovering(day)) &&
            isNextHovering(day) &&
            !isLastDayOfMonth(day) &&
            isInMonth(day)
          "
          class="absolute bottom-0 w-6 h-8 ltr:-right-4 rtl:-left-4 bg-n-blue-4 dark:bg-n-blue-5 -z-10"
        />
      </div>
    </div>

    <!-- Hint while waiting for range end click -->
    <p
      v-if="selectingEnd && !internalEnd"
      class="text-xs text-n-slate-10 text-center pb-1"
    >
      {{ $t('FILTER.DATE_RANGE_HINT') }}
    </p>
  </div>
</template>
